import React, {useEffect, useRef, useState} from 'react';
import {useForm} from 'react-hook-form';

import {
  Alert,
  Button,
  Card,
  CardContent,
  CardHeader,
  FieldError,
  Input,
  Label,
} from '@design-system';

import {useOnboarding} from '../../Onboarding/useOnboarding';
import useGetLocalAuthorities from '../../Queries/useGetLocalAuthorities';
import useHealthBoardAreas from '../../Queries/useHealthBoardAreas';
import useProSectors from '../../Queries/useProSectors';
import {PRO_REGION_TYPES} from '../../Util/constants';
import useRetryFocus from '../../Util/useRetryFocus';
import useRetryHold from '../../Util/useRetryHold';

const JOB_TITLE_ID = 'pro-onboarding-job-title';
const SECTOR_ID = 'pro-onboarding-sector';
const LOCAL_AUTHORITY_ID = 'pro-onboarding-local-authority';
const HEALTH_BOARD_AREA_ID = 'pro-onboarding-health-board-area';
const REGION_ERROR_ID = 'pro-onboarding-region-error';
const SUBMIT_ERROR_ID = 'pro-onboarding-submit-error';

const errorId = (id) => `${id}-error`;

// Geometry matches the Input primitive, raised to the 44px minimum target the
// house rule sets for anything the pointer has to hit. Colour resolves through
// the Layer 2 roles only.
const selectStyles = [
  'block h-11 w-full rounded-md border px-3 text-sm',
  'border-(--compass-border) bg-(--compass-surface) text-(--compass-on-surface)',
  'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-(--compass-focus-ring)',
  'aria-[invalid=true]:border-(--compass-border-invalid)',
].join(' ');

const regionInputId = (regionType) =>
  `pro-onboarding-region-${regionType
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '')}`;

/**
 * A reference-data-backed `<select>` that announces its own loading and failure
 * in place. The whole form used to be replaced by the bare strings
 * `'Loading...'` and `'An error has occurred.'`, which threw away anything the
 * user had already typed and told assistive technology nothing.
 *
 * A failure takes focus when it first shows only if nothing else has it, since
 * the person may be answering another question. It keeps its Retry, and focus,
 * on screen while the list is asked for again and when it fails again; a Retry
 * that loads the list hands focus to the select, unless the person has moved on.
 */
const ReferenceDataSelect = ({
  id,
  testId,
  label,
  placeholder,
  query,
  options,
  optionLabel = (option) => option.name,
  field,
  invalid,
  errorMessage,
}) => {
  const describedBy = errorMessage ? errorId(id) : undefined;
  const {retrying, retryPending, retry, loadedRef} = useRetryHold(query);
  const showFailure = query.data === undefined && Boolean(query.error || retrying);
  const {failureRef} = useRetryFocus(
    {failed: showFailure, focusFirstFailure: 'when-unfocused'},
    retry
  );

  let control;

  if (showFailure) {
    control = (
      <Alert
        ref={failureRef}
        variant="error"
        data-testid={`${testId}-error`}
        className="mt-2"
        action={
          <Button
            type="button"
            variant="outline"
            className="min-h-11"
            data-testid={`${testId}-retry`}
            aria-busy={retryPending ? true : undefined}
            onClick={retry}>
            Retry
          </Button>
        }>
        {`We couldn't load the ${label.toLowerCase()} options. Please try again.`}
      </Alert>
    );
  } else if (query.isLoading) {
    control = (
      <p role="status" data-testid={`${testId}-loading`} className="text-sm">
        Loading {label.toLowerCase()} options…
      </p>
    );
  } else {
    control = (
      <>
        <select
          id={id}
          defaultValue=""
          className={selectStyles}
          aria-invalid={invalid ? true : undefined}
          aria-describedby={describedBy}
          {...field}
          ref={(element) => {
            field?.ref?.(element);
            loadedRef.current = element;
          }}>
          <option value="">{placeholder}</option>
          {(options ?? []).map((option) => (
            <option key={`${testId}-${option.id}`} value={option.id}>
              {optionLabel(option)}
            </option>
          ))}
        </select>
        <FieldError id={errorId(id)}>{errorMessage}</FieldError>
      </>
    );
  }

  // `htmlFor` only points somewhere in the success branch: the loading and
  // failure branches render no control, and a label referencing a missing id is
  // a dangling association rather than a name.
  const hasControl = !showFailure && !query.isLoading;

  return (
    <div>
      {hasControl ? (
        <Label htmlFor={id} className="mb-1 block">
          {label}
        </Label>
      ) : (
        <p className="mb-1 block text-sm leading-none font-medium text-(--compass-on-surface)">
          {label}
        </p>
      )}
      {control}
    </div>
  );
};

export default function ProOnboardingAboutYou() {
  const {
    register,
    handleSubmit,
    formState: {errors},
    watch,
  } = useForm();
  const selectedRegionType = watch('region_type');

  const {submitProOnboardingData} = useOnboarding();

  // `submitProOnboardingData` is async with no catch of its own, so a rejected
  // submit used to be an unhandled rejection with nothing on screen and nothing
  // stopping a second attempt — and the endpoint is rate limited.
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitFailed, setSubmitFailed] = useState(false);
  const submitFailureRef = useRef(null);

  // Finish is disabled while the details are sent, which takes focus from it, so
  // each refusal takes focus and is read out, with Finish the next stop.
  useEffect(() => {
    if (submitFailed) submitFailureRef.current?.focus();
  }, [submitFailed]);

  const onSubmit = async (data) => {
    setSubmitFailed(false);
    setIsSubmitting(true);

    try {
      await submitProOnboardingData(data);
      // Deliberately left pending on success: the provider refreshes the user
      // and this surface is replaced by the Professional home, so re-enabling
      // Finish here would only ever offer a duplicate submission.
    } catch {
      setSubmitFailed(true);
      setIsSubmitting(false);
    }
  };

  const healthBoardAreasQuery = useHealthBoardAreas();

  const sectorsQuery = useProSectors();
  const localAuthoritiesQuery = useGetLocalAuthorities({staleTime: Infinity});

  // sort sectors alphabetically by sector.name
  const sortedSectors = [...(sectorsQuery.data ?? [])].sort((a, b) => (a.name > b.name ? 1 : -1));

  return (
    <div data-testid="pro-onboarding-about-you" className="flex grow flex-col justify-center">
      <div className="mx-auto w-full max-w-3xl px-4">
        <Card className="mt-8">
          <form onSubmit={handleSubmit(onSubmit)} noValidate>
            <CardHeader className="p-8 pb-0 text-center">
              <h3>About you</h3>
              <p className="body1">
                Let us know a little bit about you so that we can tailor your experience:
              </p>
            </CardHeader>
            <CardContent className="flex flex-col gap-6 p-8">
              <div>
                <Label htmlFor={JOB_TITLE_ID} className="mb-1 block">
                  Job Title
                </Label>
                <Input
                  id={JOB_TITLE_ID}
                  className="h-11"
                  aria-invalid={errors.job_title ? true : undefined}
                  aria-describedby={errors.job_title ? errorId(JOB_TITLE_ID) : undefined}
                  {...register('job_title', {required: true})}
                />
                <FieldError id={errorId(JOB_TITLE_ID)}>
                  {errors.job_title && 'Please enter your job title.'}
                </FieldError>
              </div>

              <ReferenceDataSelect
                id={SECTOR_ID}
                testId="sectors"
                label="Sector"
                placeholder="Please select your sector"
                query={sectorsQuery}
                options={sortedSectors}
                field={register('professional_sector_id', {required: true})}
                invalid={!!errors.professional_sector_id}
                errorMessage={errors.professional_sector_id && 'Please select your sector.'}
              />

              {/* Real grouping markup: the question is the group's own label,
                  which also retires the dangling `aria-labelledby` the MUI
                  RadioGroup carried and the contrast waiver on its legend. */}
              <fieldset
                className="border-0 p-0"
                aria-invalid={errors.region_type ? true : undefined}
                aria-describedby={errors.region_type ? REGION_ERROR_ID : undefined}>
                <legend className="mb-2 w-full text-center text-(--compass-on-surface)">
                  Please select the type of region that best reflects where you work:
                </legend>
                <div className="flex flex-wrap justify-center gap-x-6">
                  {PRO_REGION_TYPES.map((regionType) => (
                    <div key={`region-type-${regionType}`} className="flex items-start gap-2">
                      <input
                        type="radio"
                        id={regionInputId(regionType)}
                        value={regionType}
                        className="mt-3 size-5 shrink-0 accent-(--compass-primary) focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-(--compass-focus-ring)"
                        {...register('region_type', {required: true})}
                      />
                      <label
                        htmlFor={regionInputId(regionType)}
                        className="flex min-h-11 cursor-pointer items-center py-2.5 text-sm text-(--compass-on-surface)">
                        {regionType}
                      </label>
                    </div>
                  ))}
                </div>
                <FieldError id={REGION_ERROR_ID} className="justify-center">
                  {errors.region_type && 'Please select a region type.'}
                </FieldError>
              </fieldset>

              {selectedRegionType === 'Local Authority' && (
                <ReferenceDataSelect
                  id={LOCAL_AUTHORITY_ID}
                  testId="local-authorities"
                  label="Local Authority"
                  placeholder="Please select your local authority"
                  query={localAuthoritiesQuery}
                  options={localAuthoritiesQuery.data}
                  field={register('local_authority_id', {required: true})}
                  invalid={!!errors.local_authority_id}
                  errorMessage={errors.local_authority_id && 'Please select your local authority.'}
                />
              )}

              {selectedRegionType === 'Health Board' && (
                <ReferenceDataSelect
                  id={HEALTH_BOARD_AREA_ID}
                  testId="health-board-areas"
                  label="Health Board Area"
                  placeholder="Please select your health board area"
                  query={healthBoardAreasQuery}
                  options={healthBoardAreasQuery.data}
                  field={register('health_board_area_id', {required: true})}
                  invalid={!!errors.health_board_area_id}
                  errorMessage={
                    errors.health_board_area_id && 'Please select your health board area.'
                  }
                />
              )}

              {submitFailed && (
                <Alert
                  ref={submitFailureRef}
                  variant="error"
                  id={SUBMIT_ERROR_ID}
                  data-testid={SUBMIT_ERROR_ID}
                  className="my-0">
                  We couldn&apos;t save your details. Please try again.
                </Alert>
              )}

              <div className="flex justify-center">
                <Button
                  type="submit"
                  className="min-h-11"
                  disabled={isSubmitting}
                  aria-describedby={submitFailed ? SUBMIT_ERROR_ID : undefined}>
                  Finish
                </Button>
              </div>
            </CardContent>
          </form>
        </Card>
      </div>
    </div>
  );
}
